/**
 * Utility functions for SQD (Service Quality Dimensions) analysis
 */
import { satisfactionToNumeric } from '../components/common/constants';

/**
 * Process survey data to calculate SQD scores
 * @param {Array} surveys - Array of survey responses
 * @returns {Object} Calculated SQD scores and statistics
 */
export const processSQDData = (surveys) => {
  if (!surveys || !Array.isArray(surveys) || surveys.length === 0) {
    return {
      scores: {},
      chartData: [],
      respondentCounts: {},
      overallScore: 0
    };
  }
  
  // Initialize structures
  const scores = {};
  const counts = {};
  const totalRespondents = surveys.length;
  
  // SQD dimensions to analyze
  const dimensions = ['sqd0', 'sqd1', 'sqd2', 'sqd3', 'sqd4', 'sqd5', 'sqd6', 'sqd7', 'sqd8', 'sqd9', 'sqd10'];
  
  // Initialize scores and counts
  dimensions.forEach(dim => {
    scores[dim] = 0;
    counts[dim] = 0;
  });
  
  // Aggregate scores from surveys
  surveys.forEach(survey => {
    dimensions.forEach(dim => {
      const value = survey[dim];
      if (value && value !== 'N/A') {
        const numericValue = typeof value === 'number' ? value : satisfactionToNumeric(value);
        if (numericValue) {
          scores[dim] += numericValue;
          counts[dim]++;
        }
      }
    });
  });
  
  // Calculate averages
  const avgScores = {};
  let totalScore = 0;
  let validDimensions = 0;
  
  dimensions.forEach(dim => {
    if (counts[dim] > 0) {
      avgScores[dim] = scores[dim] / counts[dim];
      totalScore += avgScores[dim];
      validDimensions++;
    } else {
      avgScores[dim] = 0;
    }
  });
  
  // Calculate overall average
  const overallScore = validDimensions > 0 ? totalScore / validDimensions : 0;
  
  // Prepare chart data
  const chartData = dimensions.map(dim => ({
    id: dim,
    name: dim.toUpperCase(), // This assumes your sqdDimensions constant will provide display names
    score: avgScores[dim],
    respondents: counts[dim]
  }));
  
  return {
    scores: avgScores,
    chartData,
    respondentCounts: counts,
    overallScore
  };
};

/**
 * Get status category based on SQD score
 * @param {number} score - SQD score
 * @returns {string} Status category
 */
export const getStatusCategory = (score) => {
  if (score >= 4.5) return 'Very Satisfied';
    if (score >= 3.5) return 'Satisfied';
    if (score >= 2.5) return 'Neither Unsatisfied nor Satisfied';
    if (score >= 1.5) return 'Unsatisfied';
    return 'Very Unsatisfied';
};

/**
 * Create comparative trend data based on historical data
 * @param {Object} currentScores - Current period SQD scores
 * @param {Object} previousScores - Previous period SQD scores (if available)
 * @returns {Array} Trend data for analysis
 */
export const createTrendData = (currentScores, previousScores) => {
  if (!currentScores || !previousScores) {
    return [];
  }
  
  const dimensions = Object.keys(currentScores);
  const trendData = [];
  
  dimensions.forEach(dim => {
    const currentScore = currentScores[dim] || 0;
    const previousScore = previousScores[dim] || 0;
    
    if (currentScore > 0 || previousScore > 0) {
      // Calculate trend
      let trend = 'stable';
      let changePercent = 0;
      
      if (previousScore > 0) {
        changePercent = ((currentScore - previousScore) / previousScore) * 100;
        
        if (changePercent > 1) {
          trend = 'up';
        } else if (changePercent < -1) {
          trend = 'down';
        }
      }
      
      trendData.push({
        id: dim,
        dimension: dim.toUpperCase(), // Use your sqdDimensions mapping in actual implementation
        score: currentScore,
        previousScore,
        trend,
        changePercent: changePercent.toFixed(2)
      });
    }
  });
  
  // Sort by absolute change percentage (highest first)
  return trendData.sort((a, b) => Math.abs(parseFloat(b.changePercent)) - Math.abs(parseFloat(a.changePercent)));
};